<template>

  <!-- The slot reserves layout width; the aside inside it is absolutely positioned so a hover

       expansion overlays the page instead of reflowing it. Pinned = slot grows to full width. -->

  <div class="sidenav-slot" :class="{ 'is-pinned': pinned }">

    <aside

      class="sidenav"

      :class="{ 'is-expanded': expanded, 'is-pinned': pinned, 'is-hover': !pinned && hovering }"

      @mouseenter="onEnter"

      @mouseleave="onLeave"

      @focusin="onEnter"

      @focusout="onFocusOut"

      aria-label="Main navigation"

    >

      <!-- Brand -->

      <div class="sidenav-brand-row">

        <router-link to="/" class="sidenav-brand" :title="expanded ? '' : 'swiftAI'">

          <span class="sidenav-brand-mark"><i class="fa-solid fa-sparkles"></i></span>

          <img src="../assets/SwiftAI logo - 2026.svg" alt="swiftAI" class="sidenav-brand-logo" />

        </router-link>



        <!-- Pin / unpin — sits with the logo, only reachable when expanded -->

        <button

          type="button"

          class="sidenav-pin"

          :class="{ 'is-pinned': pinned }"

          :title="pinned ? 'Unpin sidebar (expand on hover)' : 'Pin sidebar open'"

          :aria-pressed="pinned ? 'true' : 'false'"

          @click="togglePin"

        >

          <i class="fa-solid fa-thumbtack"></i>

        </button>

      </div>



      <!-- Nav -->

      <nav class="sidenav-list">

        <template v-for="item in visibleItems" :key="item.label">

          <!-- Group with children -->

          <div v-if="item.group" class="sidenav-group" :class="{ 'is-open': isGroupOpen(item), 'is-active': isGroupActive(item) }">

            <button

              type="button"

              class="sidenav-item sidenav-group-trigger"

              :title="expanded ? '' : item.label"

              @click="toggleGroup(item)"

            >

              <span class="sidenav-icon"><i :class="item.icon"></i></span>

              <span class="sidenav-label">{{ item.label }}</span>

              <i class="fa-solid fa-chevron-down sidenav-chevron"></i>

            </button>



            <div class="sidenav-children" v-show="expanded && isGroupOpen(item)">

              <template v-for="(child, idx) in item.children" :key="child.section || child.to || idx">

                <div v-if="child.section" class="sidenav-section">{{ child.section }}</div>

                <router-link

                  v-else

                  :to="child.to"

                  class="sidenav-child"

                  :class="{ 'is-active': isActive(child.to) }"

                >

                  {{ child.label }}

                </router-link>

              </template>

            </div>

          </div>



          <!-- External link -->

          <a

            v-else-if="item.external"

            :href="resolveHref(item)"

            target="_blank"

            rel="noopener"

            class="sidenav-item"

            :title="expanded ? '' : item.label"

          >

            <span class="sidenav-icon"><i :class="item.icon"></i></span>

            <span class="sidenav-label">{{ item.label }}</span>

            <i class="fa-light fa-arrow-up-right-from-square sidenav-ext"></i>

          </a>



          <!-- Plain route -->

          <router-link

            v-else

            :to="item.to"

            class="sidenav-item"

            :class="{ 'is-active': isActive(item.to) }"

            :title="expanded ? '' : item.label"

          >

            <span class="sidenav-icon"><i :class="item.icon"></i></span>

            <span class="sidenav-label">{{ item.label }}</span>

          </router-link>

        </template>

      </nav>



      <!-- Chat launchers — these open the swiftChat / LoanLaunch drawers that live in the

           dashboard view (see helpers/chatLauncher.js). Expanded = stacked pills; collapsed = icons. -->

      <div class="sidenav-footer">

        <button type="button" class="sidenav-chat sidenav-chat--swift" title="swiftChat" @click="openChat(KIND_SWIFT)">

          <span class="sidenav-chat-icon"><i class="fa-duotone fa-wand-magic-sparkles"></i></span>

          <span class="sidenav-label">swiftChat</span>

        </button>



        <button type="button" class="sidenav-chat sidenav-chat--ll" title="Chat" @click="openChat(KIND_LOANLAUNCH)">

          <span class="sidenav-chat-icon">

            <img :src="loanLaunchLogo" alt="" class="sidenav-chat-logo" />

            <span v-if="unread > 0" class="sidenav-chat-dot"></span>

          </span>

          <span class="sidenav-label">Chat</span>

          <span v-if="unread > 0" class="sidenav-chat-badge">{{ unread > 999 ? '999+' : unread }}</span>

        </button>

      </div>

    </aside>

  </div>

</template>



<script>

import bus from "@/helpers/appBus.js"

import {

  requestOpenChat,

  CHAT_UNREAD_EVENT,

  CHAT_KIND_SWIFT,

  CHAT_KIND_LOANLAUNCH

} from "@/helpers/chatLauncher.js"

import { readNavPinned, writeNavPinned, subscribeNavPinned } from "@/helpers/navPrefs.js"

import {

  NAV_ITEMS,

  computeNavPermissions,

  filterByPermissions,

  isPathActive

} from "@/helpers/navConfig.js"



// Hover intent: don't flash open when the cursor merely crosses the rail, and don't snap shut

// the instant it leaves (users overshoot toward a flyout child).

const OPEN_DELAY_MS = 120

const CLOSE_DELAY_MS = 220



export default {

  name: "SideNav",

  data() {

    return {

      pinned: readNavPinned(),

      hovering: false,

      perms: computeNavPermissions(),

      openGroups: {},

      unread: 0,

      loanLaunchLogo: require("@/assets/LoanLaunchLogo.png"),

      KIND_SWIFT: CHAT_KIND_SWIFT,

      KIND_LOANLAUNCH: CHAT_KIND_LOANLAUNCH,

      openTimer: null,

      closeTimer: null,

      unsubscribePinned: null

    }

  },

  computed: {

    expanded() {

      return this.pinned || this.hovering

    },

    visibleItems() {

      return filterByPermissions(NAV_ITEMS, this.perms)

    },

    routePath() {

      return this.$route?.path || ""

    }

  },

  watch: {

    // Auto-open the group that owns the current page so the user sees where they are.

    routePath: {

      immediate: true,

      handler() {

        for (const item of NAV_ITEMS) {

          if (item.group && this.isGroupActive(item)) this.openGroups[item.label] = true

        }

      }

    }

  },

  mounted() {

    bus.on("auth-context-ready", this.refreshPermissions)

    bus.on(CHAT_UNREAD_EVENT, this.onUnread)

    this.unsubscribePinned = subscribeNavPinned(v => { this.pinned = v })

  },

  beforeUnmount() {

    bus.off("auth-context-ready", this.refreshPermissions)

    bus.off(CHAT_UNREAD_EVENT, this.onUnread)

    if (this.unsubscribePinned) this.unsubscribePinned()

    clearTimeout(this.openTimer)

    clearTimeout(this.closeTimer)

  },

  methods: {

    refreshPermissions() {

      // Roles change on impersonation; header.vue re-reads on the same bus event.

      this.perms = computeNavPermissions()

    },

    isActive(to) {

      return isPathActive(this.routePath, to)

    },

    isGroupActive(item) {

      return (item.children || []).some(c => c.to && this.isActive(c.to))

    },

    isGroupOpen(item) {

      return !!this.openGroups[item.label]

    },

    toggleGroup(item) {

      // Clicking a group while collapsed & unpinned: we're already hovering (so expanded),

      // just toggle. Nothing special needed.

      this.openGroups[item.label] = !this.openGroups[item.label]

    },

    onUnread(n) {

      this.unread = Number(n) || 0

    },

    async openChat(kind) {

      // Drawers live on the dashboard. From anywhere else, go there first; the host view

      // picks up the pending intent in its mounted().

      if (this.routePath !== "/") {

        try { await this.$router.push("/") } catch (e) {}

      }

      requestOpenChat(kind)

    },

    resolveHref(item) {

      return typeof item.href === "function" ? item.href() : item.href

    },

    togglePin() {

      this.pinned = !this.pinned

      writeNavPinned(this.pinned)

      // Unpinning while the mouse is still inside: stay open until it leaves.

      if (!this.pinned) this.hovering = true

    },

    onEnter() {

      clearTimeout(this.closeTimer)

      if (this.hovering || this.pinned) return

      this.openTimer = setTimeout(() => { this.hovering = true }, OPEN_DELAY_MS)

    },

    onLeave() {

      clearTimeout(this.openTimer)

      if (this.pinned) return

      this.closeTimer = setTimeout(() => { this.hovering = false }, CLOSE_DELAY_MS)

    },

    onFocusOut(e) {

      // Keyboard users: collapse only when focus actually leaves the sidebar.

      if (this.$el.contains(e.relatedTarget)) return

      this.onLeave()

    }

  }

}

</script>



<style scoped>

.sidenav-slot {

  --sn-collapsed: 72px;

  --sn-expanded: 236px;

  --sn-ease: 180ms cubic-bezier(0.2, 0.7, 0.2, 1);



  position: relative;

  flex: 0 0 var(--sn-collapsed);

  width: var(--sn-collapsed);

  height: 100%;

  transition: flex-basis var(--sn-ease), width var(--sn-ease);

  z-index: 60;

}



.sidenav-slot.is-pinned {

  flex-basis: var(--sn-expanded);

  width: var(--sn-expanded);

}



.sidenav {

  position: absolute;

  inset: 0 auto 0 0;

  width: var(--sn-collapsed);

  display: flex;

  flex-direction: column;

  background: var(--background-dk, #f7f7f7);

  border-right: 1px solid var(--secondary-ltr, #e6e6eb);

  box-sizing: border-box;

  overflow: hidden;

  transition: width var(--sn-ease), box-shadow var(--sn-ease);

  font-family: Segoe UI, sans-serif;

  -webkit-font-smoothing: antialiased;

}



.sidenav.is-expanded {

  width: var(--sn-expanded);

}



/* Hover-expanded (not pinned) floats over the page content */

.sidenav.is-hover {

  box-shadow: 8px 0 28px rgba(5, 29, 58, 0.12);

}



/* ── Brand ── */

.sidenav-brand {

  display: flex;

  align-items: center;

  height: 64px;

  padding: 0 18px;

  gap: 10px;

  text-decoration: none;

  flex: 0 0 auto;

  overflow: hidden;

}



.sidenav-brand-mark {

  width: 36px;

  height: 36px;

  flex: 0 0 36px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  color: #f0a437;

  font-size: 2rem;

}



.sidenav-brand-logo {

  height: 30px;

  width: auto;

  max-width: 150px;

  opacity: 0;

  transform: translateX(-6px);

  transition: opacity var(--sn-ease), transform var(--sn-ease);

}



.sidenav.is-expanded .sidenav-brand-mark { display: none; }

.sidenav.is-expanded .sidenav-brand-logo {

  opacity: 1;

  transform: none;

}



/* ── List ── */

.sidenav-list {

  flex: 1 1 auto;

  min-height: 0;

  overflow-y: auto;

  overflow-x: hidden;

  padding: 6px 12px;

  display: flex;

  flex-direction: column;

  gap: 2px;

  scrollbar-width: thin;

}



.sidenav-item {

  display: flex;

  align-items: center;

  gap: 12px;

  height: 44px;

  padding: 0 12px;

  border-radius: 10px;

  color: var(--primary, #051d3a);

  text-decoration: none;

  font-size: 1.4rem;

  font-weight: 500;

  white-space: nowrap;

  background: transparent;

  border: 0;

  cursor: pointer;

  width: 100%;

  text-align: left;

  transition: background 120ms ease, color 120ms ease;

}



.sidenav-item:hover {

  background: rgba(5, 29, 58, 0.06);

}



.sidenav-item.is-active,

.sidenav-group.is-active > .sidenav-group-trigger {

  background: rgba(5, 29, 58, 0.08);

  font-weight: 600;

}



.sidenav-icon {

  width: 24px;

  flex: 0 0 24px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  font-size: 1.7rem;

  color: #8a96a8;

  transition: color 120ms ease;

}



.sidenav-item:hover .sidenav-icon,

.sidenav-item.is-active .sidenav-icon,

.sidenav-group.is-active .sidenav-icon {

  color: var(--primary, #051d3a);

}



/* Labels fade+slide in with the expansion; collapsed they take no space */

.sidenav-label {

  flex: 1 1 auto;

  min-width: 0;

  overflow: hidden;

  text-overflow: ellipsis;

  opacity: 0;

  transform: translateX(-6px);

  transition: opacity var(--sn-ease), transform var(--sn-ease);

}



.sidenav.is-expanded .sidenav-label {

  opacity: 1;

  transform: none;

}



.sidenav-chevron,

.sidenav-ext {

  font-size: 1rem;

  color: #8a96a8;

  opacity: 0;

  transition: opacity var(--sn-ease), transform 150ms ease;

  flex: 0 0 auto;

}



.sidenav.is-expanded .sidenav-chevron,

.sidenav.is-expanded .sidenav-ext {

  opacity: 0.8;

}



.sidenav-group.is-open .sidenav-chevron {

  transform: rotate(180deg);

}



/* ── Group children ── */

.sidenav-children {

  display: flex;

  flex-direction: column;

  gap: 1px;

  padding: 2px 0 6px 0;

  margin-left: 24px;           /* aligns under the label, past the icon column */

  border-left: 1px solid var(--secondary-ltr, #e6e6eb);

}



.sidenav-child {

  display: block;

  padding: 8px 12px 8px 22px;

  margin-left: -1px;

  font-size: 1.3rem;

  color: var(--text-secondary-on-background, #4b5563);

  text-decoration: none;

  border-left: 2px solid transparent;

  border-radius: 0 8px 8px 0;

  white-space: nowrap;

  transition: background 120ms ease, color 120ms ease;

}



.sidenav-child:hover {

  background: rgba(5, 29, 58, 0.05);

  color: var(--primary, #051d3a);

}



.sidenav-child.is-active {

  color: var(--accent, #398b87);

  font-weight: 600;

  border-left-color: var(--accent, #398b87);

}



.sidenav-section {

  padding: 10px 12px 4px 22px;

  font-size: 1.05rem;

  font-weight: 800;

  letter-spacing: 0.06em;

  text-transform: uppercase;

  /* Same hue as the swiftChat pill so these read as a deliberate accent, not muted metadata */

  color: var(--primary, #051d3a);

  white-space: nowrap;

}



/* ── Footer / chat launchers ── */

.sidenav-footer {

  flex: 0 0 auto;

  padding: 10px 12px 14px;

  border-top: 1px solid var(--secondary-ltr, #e6e6eb);

  display: flex;

  flex-direction: column;

  gap: 8px;

}



.sidenav-chat {

  position: relative;

  display: flex;

  align-items: center;

  gap: 12px;

  height: 44px;

  width: 100%;

  padding: 0 12px;

  border-radius: 12px;

  border: 1px solid transparent;

  font-family: inherit;

  font-size: 1.4rem;

  font-weight: 600;

  cursor: pointer;

  white-space: nowrap;

  transition: background 120ms ease, box-shadow 120ms ease, transform 120ms ease;

}



.sidenav-chat:hover { transform: translateY(-1px); }



.sidenav-chat--swift {

  background: var(--primary, #051d3a);

  color: #fff;

  box-shadow: 0 6px 16px rgba(5, 29, 58, 0.22);

}



.sidenav-chat--swift .sidenav-chat-icon i {

  --fa-primary-color: #7dd3fc;

  --fa-secondary-color: #fde68a;

  --fa-secondary-opacity: 1;

}



.sidenav-chat--ll {

  background: #fff;

  color: var(--primary, #051d3a);

  border-color: rgba(5, 29, 58, 0.12);

  box-shadow: 0 4px 12px rgba(5, 29, 58, 0.08);

}



.sidenav-chat--ll:hover { border-color: rgba(37, 99, 235, 0.35); }



.sidenav-chat-icon {

  position: relative;

  width: 24px;

  flex: 0 0 24px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  font-size: 1.7rem;

}



.sidenav-chat-logo {

  width: 22px;

  height: 22px;

  border-radius: 6px;

  object-fit: contain;

}



/* Collapsed: the badge can't fit, so a red dot on the icon carries "unread" */

.sidenav-chat-dot {

  position: absolute;

  top: -3px;

  right: -4px;

  width: 9px;

  height: 9px;

  border-radius: 50%;

  background: #dc2626;

  border: 2px solid #fff;

  transition: opacity var(--sn-ease);

}



.sidenav.is-expanded .sidenav-chat-dot { opacity: 0; }



.sidenav-chat-badge {

  flex: 0 0 auto;

  min-width: 22px;

  padding: 0 7px;

  height: 20px;

  line-height: 20px;

  border-radius: 999px;

  background: #fee2e2;

  color: #dc2626;

  font-size: 1.15rem;

  font-weight: 700;

  text-align: center;

  opacity: 0;

  transition: opacity var(--sn-ease);

}



.sidenav.is-expanded .sidenav-chat-badge { opacity: 1; }



/* ── Brand row + pin ── */

.sidenav-brand-row {

  position: relative;          /* pin is absolute inside, so it never steals brand width */

  display: flex;

  align-items: center;

  height: 64px;

  flex: 0 0 auto;

}



.sidenav-brand-row .sidenav-brand {

  flex: 1 1 auto;

  min-width: 0;

}



/* Collapsed: the row is exactly the rail width, brand centered; keep the logo out of the way */

.sidenav:not(.is-expanded) .sidenav-brand {

  justify-content: center;

  padding: 0;

}



.sidenav:not(.is-expanded) .sidenav-brand-logo {

  display: none;

}



/* Expanded: leave room on the right for the pin */

.sidenav.is-expanded .sidenav-brand {

  padding-right: 48px;

}



.sidenav-pin {

  position: absolute;

  right: 10px;

  top: 50%;

  margin-top: -16px;

  width: 32px;

  height: 32px;

  border: 0;

  border-radius: 8px;

  background: transparent;

  color: #8a96a8;

  font-size: 1.3rem;

  cursor: pointer;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  opacity: 0;

  pointer-events: none;

  transition: opacity var(--sn-ease), background 120ms ease, color 120ms ease, transform 150ms ease;

}



.sidenav.is-expanded .sidenav-pin {

  opacity: 1;

  pointer-events: auto;

}



.sidenav-pin i { transform: rotate(45deg); }       /* unpinned: tilted */

.sidenav-pin.is-pinned i { transform: none; }      /* pinned: upright */



.sidenav-pin:hover {

  background: rgba(5, 29, 58, 0.06);

  color: var(--primary, #051d3a);

}



.sidenav-pin.is-pinned {

  color: var(--accent, #398b87);

  background: var(--accent-ltst, #e7f3f3);

}



/* ── Mobile: hide the rail; header.vue falls back to its horizontal nav ── */

@media (max-width: 630px) {

  .sidenav-slot {

    display: none;

  }

}



/* ── Dark mode ── */

.cgsdark .sidenav {

  background: var(--kendo-body-bg, #070b14);

  border-right-color: rgba(148, 163, 184, 0.15);

}



.cgsdark .sidenav.is-hover {

  box-shadow: 8px 0 28px rgba(0, 0, 0, 0.5);

}



.cgsdark .sidenav-item,

.cgsdark .sidenav-pin:hover {

  color: rgba(226, 232, 240, 0.9);

}



.cgsdark .sidenav-pin.is-pinned {

  background: rgba(57, 139, 135, 0.18);

  color: var(--accent-lt, #6fb8b4);

}



.cgsdark .sidenav-chat--ll {

  background: rgba(226, 232, 240, 0.06);

  color: rgba(226, 232, 240, 0.92);

  border-color: rgba(148, 163, 184, 0.2);

}



.cgsdark .sidenav-chat--swift {

  background: rgba(19, 106, 211, 0.35);

}



.cgsdark .sidenav-chat-dot { border-color: var(--kendo-body-bg, #070b14); }



.cgsdark .sidenav-item:hover,

.cgsdark .sidenav-pin:hover,

.cgsdark .sidenav-child:hover {

  background: rgba(226, 232, 240, 0.06);

}



.cgsdark .sidenav-item.is-active,

.cgsdark .sidenav-group.is-active > .sidenav-group-trigger {

  background: rgba(226, 232, 240, 0.09);

}



.cgsdark .sidenav-item:hover .sidenav-icon,

.cgsdark .sidenav-item.is-active .sidenav-icon,

.cgsdark .sidenav-group.is-active .sidenav-icon {

  color: rgba(226, 232, 240, 0.95);

}



.cgsdark .sidenav-child {

  color: rgba(148, 163, 184, 0.9);

}



.cgsdark .sidenav-section {

  color: var(--primary-mid, #126ad3);

}



.cgsdark .sidenav-child:hover {

  color: rgba(226, 232, 240, 0.95);

}



.cgsdark .sidenav-child.is-active {

  color: color-mix(in srgb, var(--accent) 70%, rgba(226, 232, 240, 0.95));

  border-left-color: var(--accent);

}



.cgsdark .sidenav-children,

.cgsdark .sidenav-footer {

  border-color: rgba(148, 163, 184, 0.15);

}



.cgsdark .sidenav-brand-logo {

  filter: brightness(0) invert(0.92);

}

</style>